import React, { Suspense, useEffect } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
  Redirect,
  RouteProps,
} from 'react-router-dom'
import './App.scss'
import { useOnce } from './hooks'
import * as login from './requests/auth'
import * as eula from './requests/eula'
import { ForwardUrl } from './requests/fowardUrl'
import { RequestedBoolean, Errors, EulaState } from './types'
import ErrorPage from './ErrorPage/ErrorPage'

const Login = React.lazy(async () => import('./Login/Login'))

const Eula = React.lazy(async () => import('./eula/StaticEula'))

const Landing = React.lazy(async () => import('./landing/Landing'))
const Route404 = React.lazy(async () => import('./404/404'))

type State = {
  isLoggedIn: RequestedBoolean
  isEulaRead: RequestedBoolean | { rejected: true }
  privateLabelName: string | { neverSet: true } | null
  criticalRequestsUnreachable: string | null
}

const initialState = {
  isLoggedIn: null,
  isEulaRead: null,
  privateLabelName: null,
  criticalRequestsUnreachable: null,
}

type Action =
  | {
      type: 'SET_FIELD'
      key: keyof State
      value: State[keyof State]
    }
  | {
      type: 'SET_MULTIPLE_FIELDS'
      fields: Partial<State>
    }

const reducer = (state: State, action: Action): State => {
  if (process.env.NODE_ENV !== 'production') {
    console.group('Update')
    console.log(action)
    console.log(state)
    console.groupEnd()
  }

  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        [action.key]: action.value,
      }
    case 'SET_MULTIPLE_FIELDS':
      return {
        ...state,
        ...action.fields,
      }
  }
}

// https://stackoverflow.com/a/43171515/1924257
const PrivateRoute: React.FunctionComponent<{
  isLoggedIn: RequestedBoolean
} & RouteProps> = props => {
  const { isLoggedIn, ...routeProps } = props

  return (
    <Route
      {...routeProps}
      render={props => {
        if (isLoggedIn === true) {
          return routeProps.render!(props)
        }

        return (
          <Redirect
            to={{ pathname: '/login', state: { from: props.location } }}
          />
        )
      }}
    />
  )
}

function LazyRoutes() {
  const [state, dispatch] = React.useReducer(reducer, initialState),
    { isLoggedIn, isEulaRead, privateLabelName } = state,
    history = useHistory()

  useOnce(ForwardUrl.setOnce)

  useEffect(() => {
    const pathname = history.location.pathname,
      plPattern = /^\/login\/([^/]*)\/?$/g,
      plMatches = plPattern.exec(pathname),
      justLoginPath = /^\/login\/?$/.test(pathname)

    let privateLabelName = plMatches ? plMatches[1] : null

    if (justLoginPath) {
      // Empty string will trigger a request to the back end unlike null or {neverSet: true}
      privateLabelName = ''
    } else if (privateLabelName == null) {
      dispatch({
        type: 'SET_FIELD',
        key: 'privateLabelName',
        value: { neverSet: true },
      })

      return
    }

    dispatch({
      type: 'SET_MULTIPLE_FIELDS',
      fields: {
        privateLabelName,
      },
    })
  }, [history.location.pathname])

  useOnce(async () => {
    const nextPage = await (async () => {
      const isLoggedIn = await login.checkIfLoggedIn()
      const { pathname, search: queryParams } = history.location

      dispatch({
        type: 'SET_FIELD',
        key: 'isLoggedIn',
        value: isLoggedIn,
      })

      switch (isLoggedIn) {
        case 'failed': {
          dispatch({
            type: 'SET_FIELD',
            key: 'criticalRequestsUnreachable',
            value: Errors.authCheckFailed,
          })
          return `${pathname}${queryParams}`
        }
        case null:
        case false: {
          if (pathname.startsWith('/login')) {
            return `${pathname}${queryParams}`
          }

          return '/login'
        }
        case true: {
          break
        }
      }

      const eulaRead = await eula.isEulaRead()

      dispatch({
        type: 'SET_FIELD',
        key: 'isEulaRead',
        value: eulaRead,
      })

      if (!eulaRead) {
        return '/login'
      }

      if (history.location.pathname.startsWith('/login')) {
        return '/'
      }

      const forwardUrl = ForwardUrl.get()

      if (forwardUrl != null) {
        ForwardUrl.clear()

        return forwardUrl
      }

      return history.location.pathname
    })()

    history.replace(nextPage)
  })

  useEffect(() => {
    const findForwardFragment = () => {
      return window.location
      .search
      .substring(1) // Skip past the initial ?
      .split('&')
      .find(s => s.startsWith('forward='))
      ?.split('=')[1]
      .substring(1) ?? ''
    }

    const buildConsoleUrl = () => {
      if (window.location.hostname.endsWith('aws.cwan.io') && !window.location.hostname.startsWith('localhost')) {
        return 'https://console.uswe2.beta.aws.cwan.io/'
      } else if (window.location.hostname.startsWith('stg')) {
        return 'https://stg1-console.clearwateranalytics.com'
      }

      return 'https://console.clearwateranalytics.com/'
    }

    /*
    For lower environments/localhost, we're going to continue to allow the old username/password login
    path to enable testing. Once we figure out how to make test users/localhost development work with
    the SSO authorization workflow, this list of exceptions should be removed.
    */
   const isLegacyLoginHost = () => {
    const legacyLoginHosts = ['localhost', 'localhost.aws.cwan.io', 'im.uswe2.beta.aws.cwan.io', 'stg1-im.clearwateranalytics.com', 'stg2-im.clearwateranalytics.com']

    return legacyLoginHosts.includes(window.location.hostname)
   }
    
    if (isLoggedIn === false && !isLegacyLoginHost()) {
      window.location.href = buildConsoleUrl()
        + "?forward="
        + encodeURIComponent(window.location.origin + '/')
        + encodeURIComponent(findForwardFragment())
    }
  }, [isLoggedIn])

  switch (isLoggedIn) {
    case null:
      return null
    case false:
    case true:
      break
    default:
    case 'failed':
      return <ErrorPage />
  }

  return (
    <Suspense fallback={null}>
      <Switch>
        <Route
          path="/login"
          render={({ history }) => {

            const setLoggedIn = (value: RequestedBoolean) =>
              dispatch({
                type: 'SET_FIELD',
                key: 'isLoggedIn',
                value,
              })

            const setEulaRead = (value: EulaState) =>
              dispatch({
                type: 'SET_FIELD',
                key: 'isEulaRead',
                value,
              })

            const normalizedPrivateLabel =
              typeof privateLabelName === 'object'
                ? null
                : privateLabelName

            return (
              <Login
                history={history}
                isLoggedIn={isLoggedIn}
                isEulaRead={isEulaRead}
                privateLabelName={normalizedPrivateLabel}
                setEulaRead={setEulaRead}
                setLoggedIn={setLoggedIn}
              />
            )
          }}
        />
        <PrivateRoute
          isLoggedIn={isLoggedIn}
          exact
          path="/eula"
          component={Eula}
        />
        <PrivateRoute
          isLoggedIn={isLoggedIn}
          render={() => <Landing />}
          exact
          path="/"
        />
        <PrivateRoute
          isLoggedIn={isLoggedIn}
          exact
          render={({ history }) => <Route404 history={history} />}
        />
      </Switch>
    </Suspense>
  )
}

function App() {
  return (
    <div className="app-body">
      <Router>
        <LazyRoutes />
      </Router>
    </div>
  )
}

export default App
